import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ParamMap } from '@angular/router';
import { Block } from '@app/core/models/block/block.model';
import { GlobalService } from '@app/core/services/common/global.service';
import { FiltersService } from '@app/shared/services/filters/filters.service';
import { ListDetailsService } from '@app/shared/services/list/list-details.service';
import { BlockTypeEnum } from '@core/enums';
import { ViewPart } from '@core/models';
import { map, take } from 'rxjs/operators';
import { FilterService } from './filter.service';

@Component({
  selector: 'app-filter-slidable',
  templateUrl: './filter-slidable.component.html',
  styleUrls: ['./filter-slidable.component.scss'],
  standalone: false,
})
export class FilterSlidableComponent implements OnInit, AfterViewInit {
  @Input() item: Block;
  @Input() blockType: number;
  blockTypeEnum = BlockTypeEnum;

  // Loading state check
  get isLoading(): boolean {
    return ListDetailsService.isLoading.value;
  }

  typeItem = 0;
  sliderName: string;
  sliderClassName = 'sliderClassName';
  blockParent = 'blockParent';
  blockTitle = 'blockTitle';
  blockSlider = 'blockSlider';
  blockInput = 'blockInput';
  inputBlock = 'inputBlock';
  inputStart = 'inputStart';
  inputline = 'inputline';
  inputEnd = 'inputEnd';
  inputClin = 'inputClin';
  matSlider = 'matSlider';
  blockSliderLineOnOff = false;

  constructor(
    private fs: FiltersService,
    public localfs: FilterService,
    private el: ElementRef,
    private renderer: Renderer2,
    private globalService: GlobalService,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngAfterViewInit(): void {
    // Subscribe to URL parameter changes to sync filter values with current URL state
    this.fs.filtersFrom$.subscribe(params => {
      this.syncFiltersWithUrl(params);
    });

    // Subscribe to data updates from API to update min/max range values
    this.fs.listFilterSlidable.subscribe(data => {
      if (!data || data.length === 0) {
        return;
      }
      this.updateMinMaxValues(data[0]);
    });

    this.typeItem = this.item.type;
    this.setStyle(window.innerWidth, this.item);
  }

  ngOnInit(): void {
    this.sliderClassName = 'sliderClassName' + this.item.type;
    this.blockParent = 'blockParent' + this.item.type;
    this.blockTitle = 'blockTitle' + this.item.type;
    this.blockSlider = 'blockSlider' + this.item.type;
    this.blockInput = 'blockInput' + this.item.type;
    this.inputBlock = 'inputBlock' + this.item.type;
    this.inputStart = 'inputStart' + this.item.type;
    this.inputline = 'inputline' + this.item.type;
    this.inputEnd = 'inputEnd' + this.item.type;
    this.matSlider = 'matSlider' + this.item.type;
    this.inputClin = 'inputClin' + this.item.type;

    this.globalService.mediaQuery.subscribe(() => {
      this.setStyle(window.innerWidth, this.item);
    });

    if (this.item && this.item.children.length > 0) {
      this.item.children.forEach(e => {
        if (e?.settings?.viewPart == 'line-range-parent') {
          this.blockSliderLineOnOff = true;
        }
      });
    }
  }

  /**
   * Synchronize filter values with URL parameters
   */
  private syncFiltersWithUrl(params: ParamMap): void {
    if (!params) {
      return;
    }
    if (this.item.type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
      const priceFrom = params.get('priceFrom');
      const priceEnd = params.get('priceEnd');

      if (priceFrom && priceEnd) {
        this.localfs.filterStart[this.item.type] = +priceFrom;
        this.localfs.filterEnd[this.item.type] = +priceEnd;
      } else {
        this.resetToDefaultValues();
      }
      this.cdr.detectChanges();
    }

    if (this.item.type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
      const yearFrom = params.get('yearFrom');
      const yearEnd = params.get('yearEnd');

      if (yearFrom && yearEnd) {
        this.localfs.filterStart[this.item.type] = +yearFrom;
        this.localfs.filterEnd[this.item.type] = +yearEnd;
      } else {
        this.resetToDefaultValues();
      }
      this.cdr.detectChanges();
    }
  }

  /**
   * Update min/max values from API response
   */
  private updateMinMaxValues(data: any): void {
    if (!data) {
      return;
    }

    if (this.item.type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
      const newMinPrice = +data.minPrice;
      const newMaxPrice = +data.maxPrice;

      // Always update min/max values from API, even if they are 0,0
      // This ensures we have the correct range from the backend
      this.localfs.minValueStart[this.item.type] = newMinPrice;
      this.localfs.maxValueEnd[this.item.type] = newMaxPrice;

      // Clear cache when min/max values change
      this.sliderChangeCache.delete(this.item.type);

      // Initialize filter values if they are null/undefined
      this.initializeFilterValues();
      this.cdr.detectChanges();
    }

    if (this.item.type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
      const newMinYear = +data.minYearOfCreation;
      const newMaxYear = +data.maxYearOfCreation;

      // Always update min/max values from API, even if they are 0,0
      // This ensures we have the correct range from the backend
      this.localfs.minValueStart[this.item.type] = newMinYear;
      this.localfs.maxValueEnd[this.item.type] = newMaxYear;

      // Clear cache when min/max values change
      this.sliderChangeCache.delete(this.item.type);

      // Initialize filter values if they are null/undefined
      this.initializeFilterValues();
      this.cdr.detectChanges();
    }
  }

  /**
   * Initialize filter values to default (min-max range) if they are null/undefined
   */
  private initializeFilterValues(): void {
    const currentStart = this.localfs.filterStart[this.item.type];
    const currentEnd = this.localfs.filterEnd[this.item.type];
    const minValue = this.localfs.minValueStart[this.item.type];
    const maxValue = this.localfs.maxValueEnd[this.item.type];

    // Only initialize if values are null/undefined and min/max are available
    if ((currentStart === null || currentStart === undefined) &&
      (currentEnd === null || currentEnd === undefined) &&
      minValue !== null && minValue !== undefined && minValue !== 'start' &&
      maxValue !== null && maxValue !== undefined) {

      // If API returned 0,0 (no results), set to a reasonable default range
      if (minValue === 0 && maxValue === 0) {
        if (this.item.type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
          // For price, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 0;
          this.localfs.filterEnd[this.item.type] = 10000;
        } else if (this.item.type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
          // For year, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 1900;
          this.localfs.filterEnd[this.item.type] = new Date().getFullYear();
        }
      } else {
        // Use API values
        this.localfs.filterStart[this.item.type] = +minValue;
        this.localfs.filterEnd[this.item.type] = +maxValue;
      }
    }
  }

  /**
   * Reset filter values to default (min-max range)
   */
  private resetToDefaultValues(): void {
    const minValue = this.localfs.minValueStart[this.item.type];
    const maxValue = this.localfs.maxValueEnd[this.item.type];

    if (minValue !== null && minValue !== undefined && minValue !== 'start' &&
      maxValue !== null && maxValue !== undefined) {

      // If API returned 0,0 (no results), set to a reasonable default range
      if (minValue === 0 && maxValue === 0) {
        if (this.item.type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
          // For price, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 0;
          this.localfs.filterEnd[this.item.type] = 10000;
        } else if (this.item.type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
          // For year, use a reasonable default range
          this.localfs.filterStart[this.item.type] = 1900;
          this.localfs.filterEnd[this.item.type] = new Date().getFullYear();
        }
      } else {
        // Use API values
        this.localfs.filterStart[this.item.type] = +minValue;
        this.localfs.filterEnd[this.item.type] = +maxValue;
      }

      // Clear cache when filter values change
      this.sliderChangeCache.delete(this.item.type);
    }
  }

  // Nowa metoda do wyodrębnienia liczby z ciągu znaków
  extractNumber(value: string): number {
    return parseInt(value.replace(/\D/g, ''), 10);
  }

  setStyle(innerWidth, item) {
    const applyStyles = (selector, styleObj) => {
      const elements = document.querySelectorAll(selector);
      for (const key in styleObj) {
        elements.forEach(element => {
          this.renderer.setStyle(element, this.camelToKebabCase(key), styleObj[key]);
        });
      }
    };

    const styleMapping = {
      [ViewPart.SLIDER_RANGE_TITLE]: {
        selector: `.sliderClassName${this.typeItem}`,
        process: () => {
          this.sliderName = item.content;
          return item.styles.all.basic;
        },
      },
      [ViewPart.SLIDER_RANGE_LINE]: {
        selector: `.matSlider${this.typeItem}`,
        process: () => {
          const styleLine = this.parseStyle(item, innerWidth);
          const borderBottomWidthNumber = (this.extractNumber(styleLine?.borderBottomWidth) >= 3) ? -1 : 3 - this.extractNumber(styleLine?.borderBottomWidth);
          this.el.nativeElement.style.setProperty(`--lineColor`, styleLine?.borderBottomColor);
          this.el.nativeElement.style.setProperty(`--heightLine`, styleLine?.borderBottomWidth);
          this.el.nativeElement.style.setProperty(`--stylLine`, styleLine?.borderBottomStyle);
          this.el.nativeElement.style.setProperty(`--borderBottomWidthNumber`, `${borderBottomWidthNumber}px`);
          return Object.fromEntries(Object.entries(styleLine).filter(([key]) => !key.includes('border')));
        },
      },
      [ViewPart.SLIDER_RANGE_INPUT_START]: {
        selector: `.inputStart${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT_LINE]: {
        selector: `.inputline${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT_END]: {
        selector: `.inputEnd${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_PARENT]: {
        selector: `.blockTitle${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.LINE_RANGE_PARENT]: {
        selector: `.blockSlider${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
      [ViewPart.SLIDER_RANGE_INPUT]: {
        selector: `.blockInput${this.typeItem}`,
        process: () => this.parseStyle(item, innerWidth),
      },
    };

    const mapping = styleMapping[item.settings?.viewPart];
    if (mapping) {
      const styles = mapping.process();
      applyStyles(mapping.selector, styles);
    }

    item.children.forEach(e => this.setStyle(window.innerWidth, e));
    this.fs.paramsGlobalClean = false;
  }

  parseStyle(item, innerWidth) {
    const baseStyle = item?.styles?.all?.basic || {};
    let responsiveStyle = {};
    if (innerWidth <= 991 && innerWidth >= 480) {
      responsiveStyle = item?.styles?.maxWidth991Px?.basic || {};
    } else if (innerWidth <= 479) {
      responsiveStyle = item?.styles?.maxWidth479Px?.basic || {};
    }
    const finalStyle = {...baseStyle, ...responsiveStyle};
    return finalStyle;
  }

  // Cache for sliderChange results to avoid unnecessary recalculations
  private sliderChangeCache = new Map<number, boolean>();
  // Debounce timer to limit excessive calls
  private sliderChangeTimer: any = null;
  // Last calculation time to prevent excessive calls
  private lastCalculationTime = 0;

  sliderChange(type) {
    // Only process if this is the correct filter type
    if (type !== this.item.type) {
      return false;
    }

    // Check cache first
    if (this.sliderChangeCache.has(type)) {
      const cachedResult = this.sliderChangeCache.get(type);
      return cachedResult;
    }

    // Limit calculations to once per 100ms
    const now = Date.now();
    if (now - this.lastCalculationTime < 100) {
      return this.sliderChangeCache.get(type) || false;
    }

    // Debounce excessive calls
    if (this.sliderChangeTimer) {
      clearTimeout(this.sliderChangeTimer);
    }

    this.sliderChangeTimer = setTimeout(() => {
      this.performSliderChange(type);
    }, 50); // 50ms debounce

    // Return cached result immediately if available
    return this.sliderChangeCache.get(type) || false;
  }

  private performSliderChange(type: number): void {
    // Update last calculation time
    this.lastCalculationTime = Date.now();

    // Check if min/max values are loaded from API
    const minValue = this.localfs.minValueStart[type];
    const maxValue = this.localfs.maxValueEnd[type];

    if (!minValue || !maxValue || minValue === 'start' || maxValue === null) {
      this.sliderChangeCache.set(type, false);
      return;
    }

    // Check if current filter values are different from default API values
    const currentStart = this.localfs.filterStart[type];
    const currentEnd = this.localfs.filterEnd[type];

    // Convert to numbers for proper comparison
    const minApiValue = +minValue;
    const maxApiValue = +maxValue;
    const currentStartNum = +currentStart;
    const currentEndNum = +currentEnd;

    // Show clear button only if values are different from API defaults
    const startDifferent = currentStartNum !== minApiValue;
    const endDifferent = currentEndNum !== maxApiValue;
    const result = startDifferent || endDifferent;

    // Cache the result
    this.sliderChangeCache.set(type, result);
  }

  onInputChange(type, key, inputStatAmount, inputEndAmount) {
    // Don't filter during typing - only update UI
    this.cdr.detectChanges();
  }

  onSliderInput(type, key, inputStatAmount, inputEndAmount) {
    // Don't filter during slider movement - only update UI
    this.cdr.detectChanges();
  }

  onClearClick(type) {
    // Sprawdź czy trwa ładowanie
    if (this.isLoading) {
      return;
    }

    this.clearFilter(type);
  }

  onSliderChange(type, key, inputStatAmount, inputEndAmount) {
    // Prevent filter changes during loading to avoid race conditions
    if (this.isLoading) {
      return;
    }

    // Clear cache when slider values change
    this.sliderChangeCache.delete(type);

    // Handle clear operation (key === 'all') - removes filter parameters from URL
    if (key === 'all') {
      this.clearFilterFromUrl(type);
    } else {
      // Normal filter operation - validate and apply filter
      const isValid = this.validInput(type, key);

      if (isValid) {
        this.applyFilter(type);
      }
    }
  }

  /**
   * Clear filter values and reset to default
   */
  private clearFilter(type: number): void {
    this.localfs.filterStart[type] = this.localfs.minValueStart[type];
    this.localfs.filterEnd[type] = this.localfs.maxValueEnd[type];
    // Clear cache when filter values change
    this.sliderChangeCache.delete(type);
    this.onSliderChange(type, 'all', this.localfs.filterStart[type], this.localfs.filterEnd[type]);
  }

  /**
   * Clear filter from URL by removing its parameters
   */
  private clearFilterFromUrl(type: number): void {
    this.fs.filtersFrom$.pipe(
      take(1),
      map((params: ParamMap) => {
        const paramsCopy = {};
        params.keys.forEach(k => {
          if (type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
            if (k !== 'yearFrom' && k !== 'yearEnd') {
              paramsCopy[k] = params.get(k);
            }
          } else if (type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
            if (k !== 'priceFrom' && k !== 'priceEnd') {
              paramsCopy[k] = params.get(k);
            }
          }
        });
        return paramsCopy;
      }),
    ).subscribe(filteredParams => {
      this.fs.goTo(filteredParams);
    });
  }

  /**
   * Apply filter by sending search parameters
   */
  private applyFilter(type: number): void {
    if (type === this.blockTypeEnum.SLIDER_PRICE_RANGE_TYPE) {
      const priceRange = {
        priceFrom: this.localfs.filterStart[type],
        priceEnd: this.localfs.filterEnd[type],
      };
      this.fs.search(priceRange);
    }

    if (type === this.blockTypeEnum.SLIDER_YEAR_OF_CREATION) {
      const yearRange = {
        yearFrom: this.localfs.filterStart[type],
        yearEnd: this.localfs.filterEnd[type],
      };
      this.fs.search(yearRange);
    }
  }

  camelToKebabCase(str: string): string {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  validateAndCorrectInput(type, key) {
    if (key === 'start') {
      const value = this.localfs.filterStart[type];
      if (value < this.localfs.minValueStart[type]) {
        this.localfs.filterStart[type] = this.localfs.minValueStart[type];
      } else if (value > this.localfs.maxValueEnd[type]) {
        this.localfs.filterStart[type] = this.localfs.maxValueEnd[type];
      }
    }

    if (key === 'end') {
      const value = this.localfs.filterEnd[type];
      if (value < this.localfs.minValueStart[type]) {
        this.localfs.filterEnd[type] = this.localfs.minValueStart[type];
      } else if (value > this.localfs.maxValueEnd[type]) {
        this.localfs.filterEnd[type] = this.localfs.maxValueEnd[type];
      }
    }

    // Trigger filter update after correction
    this.onSliderChange(type, key, this.localfs.filterStart[type], this.localfs.filterEnd[type]);
  }

  validInput(type, key) {
    if (key === 'start') {
      const value = this.localfs.filterStart[type];
      return value !== null && value !== undefined &&
        this.localfs.minValueStart[type] <= value && value <= this.localfs.maxValueEnd[type];
    }
    if (key === 'end') {
      const value = this.localfs.filterEnd[type];
      return value !== null && value !== undefined &&
        this.localfs.minValueStart[type] <= value && value <= this.localfs.maxValueEnd[type];
    }
    if (key === 'all') {
      // For 'all' key, check if both start and end values are at their default positions
      const startAtDefault = this.localfs.filterStart[type] === this.localfs.minValueStart[type];
      const endAtDefault = this.localfs.filterEnd[type] === this.localfs.maxValueEnd[type];
      return startAtDefault && endAtDefault;
    }
    return false;
  }
}
